import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';


const NutritionChart = ({ weeklyData = [], macroData = null, loading = false }) => {
  const [activeTab, setActiveTab] = useState('calories');

  const defaultWeekly = [
    { day: 'Mon', calories: 0, protein: 0, carbs: 0, fat: 0 },
    { day: 'Tue', calories: 0, protein: 0, carbs: 0, fat: 0 },
    { day: 'Wed', calories: 0, protein: 0, carbs: 0, fat: 0 },
    { day: 'Thu', calories: 0, protein: 0, carbs: 0, fat: 0 },
    { day: 'Fri', calories: 0, protein: 0, carbs: 0, fat: 0 },
    { day: 'Sat', calories: 0, protein: 0, carbs: 0, fat: 0 },
    { day: 'Sun', calories: 0, protein: 0, carbs: 0, fat: 0 }
  ];

  const chartData = weeklyData?.length > 0 ? weeklyData : defaultWeekly;

  // Today's macronutrient breakdown from real data or defaults
  const totalMacroGrams = (macroData?.protein || 0) + (macroData?.carbs || 0) + (macroData?.fat || 0);
  const pieData = totalMacroGrams > 0
    ? [
        { name: 'Protein', value: Math.round((macroData?.protein / totalMacroGrams) * 100), grams: Math.round(macroData?.protein), color: '#10B981' },
        { name: 'Carbs', value: Math.round((macroData?.carbs / totalMacroGrams) * 100), grams: Math.round(macroData?.carbs), color: '#3B82F6' },
        { name: 'Fat', value: Math.round((macroData?.fat / totalMacroGrams) * 100), grams: Math.round(macroData?.fat), color: '#F59E0B' }
      ]
    : [
        { name: 'Protein', value: 33, grams: 0, color: '#10B981' },
        { name: 'Carbs', value: 34, grams: 0, color: '#3B82F6' },
        { name: 'Fat', value: 33, grams: 0, color: '#F59E0B' }
      ];

  const tabs = [
    { id: 'calories', label: 'Calories', icon: 'Zap' },
    { id: 'macros', label: 'Macros', icon: 'PieChart' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border/40 rounded-lg p-3 shadow-wellness elevation-2">
          <p className="font-medium text-foreground">{label}</p>
          <p className="text-sm text-primary">
            {activeTab === 'calories' ? 'Calories: ' : 'Value: '}
            <span className="font-semibold">{payload?.[0]?.value}</span>
            {activeTab === 'calories' ? ' kcal' : 'g'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Nutrition Analytics</h3>
        <div className="flex space-x-1 bg-muted/30 rounded-lg p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading nutrition data...</p>
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'calories' && (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#6B7280"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#6B7280"
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="calories" 
                    fill="#5DB075" 
                    radius={[4, 4, 0, 0]}
                    className="hover:opacity-80 transition-opacity"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {activeTab === 'macros' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [`${value}% (${props?.payload?.grams}g)`, name]}
                      labelStyle={{ color: '#1F2937' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-medium text-foreground">Today's Breakdown</h4>
                {pieData?.map((macro, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: macro?.color }}
                      />
                      <span className="text-sm font-medium text-foreground">{macro?.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-foreground">{macro?.grams}g</span>
                      <span className="text-xs text-muted-foreground ml-1">({macro?.value}%)</span>
                    </div>
                  </div>
                ))}
                
                <div className="pt-4 border-t border-border/40">
                  <div className="text-xs text-muted-foreground text-center">
                    {totalMacroGrams > 0 ? `Total: ${Math.round(totalMacroGrams)}g macros today` : 'No macros logged today'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NutritionChart;